'use client';

import React from 'react';
import { Button, Card } from '@ui';
import { t } from '@utils/translate';
import { renderTranslation } from '@utils/renderTranslation';
import type { CarouselItem } from '../carousels/CarouselData';
import { portfolioPublicPath } from '@lib/portfolioBreadcrumbs';
import ImageCardContent from './ImageCardContent';
import ImageCardModal from './ImageCardModal';

const DEFAULT_IMAGE_URL = '/images/app-icons/logo.svg';

export interface ImageCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Carousel item data (passed as `carousel-item` in JSX). */
  'carousel-item'?: CarouselItem;
  /** Optional 1-based index (e.g. from carousel); reserved for future use. */
  'carousel-index'?: number;
  /** Resolved by the page; defaults to English when omitted. */
  locale?: 'en' | 'es';
}

const ImageCard: React.FC<ImageCardProps> = ({
  'carousel-item': carouselItem,
  'carousel-index': _carouselIndex,
  className,
  style,
  locale = 'en',
  ...rest
}) => {
  const viewImageLabel = String(
    renderTranslation(t('actions.view_image', 'global', locale)) || 'View Image'
  );
  const viewSiteLabel = String(
    renderTranslation(t('actions.view_site', 'global', locale)) || 'Visit Site'
  );
  const viewMoreDetailsLabel = String(
    renderTranslation(t('actions.view_more_details', 'global', locale)) || 'View More Details'
  );
  const label = carouselItem?.title ?? 'Untitled';
  const description = carouselItem?.label ?? '';
  const rawSrc1x = carouselItem?.image_url?.trim();
  const rawSrc2x = carouselItem?.image_url_2x?.trim();
  const effective1x = rawSrc1x || DEFAULT_IMAGE_URL;
  const isDefaultImage = !rawSrc1x;
  const hasOverlay = Boolean(carouselItem?.hasOverlay);
  const overlayDescription = carouselItem?.description_long?.trim() || description;
  const [isOverlayOpen, setIsOverlayOpen] = React.useState(false);
  const hasPageSlug = Boolean(carouselItem?.hasPageSlug);
  const pageSlug = carouselItem?.pageSlug?.trim();
  const sectionSlug = carouselItem?.sectionSlug?.trim();
  const [use2x, setUse2x] = React.useState(false);
  React.useEffect(() => {
    // Only attempt @2x selection in the browser.
    // Prefer @2x on high-DPI screens when available.
    if (!rawSrc2x) return;
    const dpr = typeof window !== 'undefined' ? (window.devicePixelRatio ?? 1) : 1;
    setUse2x(dpr >= 2);
  }, [rawSrc2x]);

  const rawSrc = use2x && rawSrc2x ? rawSrc2x : effective1x;
  const href = carouselItem?.url?.trim();
  const target = carouselItem?.target?.trim();
  const rel = carouselItem?.rel?.trim() || (target === '_blank' ? 'noreferrer' : undefined);

  const handleOverlayActivate = React.useCallback(() => {
    if (href) {
      if (target === '_blank') {
        const features = rel?.includes('noreferrer') ? 'noopener,noreferrer' : 'noopener';
        window.open(href, '_blank', features);
        return;
      }
      window.location.assign(href);
      return;
    }
    setIsOverlayOpen(true);
  }, [href, rel, target]);

  const body = (
    <div className='image-container'>
      {hasOverlay ? (
        <div
          className='image-overlay-trigger'
          role='button'
          tabIndex={0}
          aria-label={viewImageLabel}
          onClick={handleOverlayActivate}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleOverlayActivate();
            }
          }}
        >
          <ImageCardContent
            rawSrc={rawSrc}
            label={label}
            isDefaultImage={isDefaultImage}
            description={description}
          />
          <div className='image-content-actions'>
            <span className='btn btn-primary'>{viewImageLabel}</span>
          </div>
        </div>
      ) : (
        <ImageCardContent
          rawSrc={rawSrc}
          label={label}
          isDefaultImage={isDefaultImage}
          description={description}
        />
      )}
      {hasPageSlug && pageSlug && sectionSlug ? (
        <div className='image-content-actions'>
          <Button
            as='a'
            href={portfolioPublicPath(locale, sectionSlug, pageSlug)}
            target='_self'
            label={viewMoreDetailsLabel}
            className={['btn-sm', hasOverlay ? 'btn-ghost' : 'btn-primary'].join(' ')}
          />
        </div>
      ) : null}
      {href ? (
        <div className='image-content-actions'>
          <Button
            as='a'
            href={href}
            target={target || undefined}
            rel={rel}
            className='btn-sm btn-ghost'
            label={viewSiteLabel}
          />
        </div>
      ) : null}
    </div>
  );

  return (
    <Card
      {...rest}
      className={['image-card', hasOverlay ? 'has-overlay' : '', className]
        .filter(Boolean)
        .join(' ')}
      style={style}
    >
      {body}
      <ImageCardModal
        open={isOverlayOpen}
        onClose={() => setIsOverlayOpen(false)}
        title={label}
        rawSrc={rawSrc}
        overlayDescription={overlayDescription}
      />
    </Card>
  );
};

export default ImageCard;
